import { useApp } from '@/App';
import { DocumentItem } from '@/components/modals/DocumentModal';
import { useModals } from '@/components/modals/ModalProvider';
import useQuerySelect from '@/hooks/useQuerySelect';
import DataRow from '@/libs/antd/components/DataRow';
import FleetModal from '@/libs/antd/components/Modal';
import FleetTitle from '@/libs/antd/components/Title';
import ViewManagementShareholder from '@/libs/antd/components/ViewManagementShareholder';
import { FormAlert, useForm } from '@/libs/antd/form';
import ANTD_DEFAULT_PROPS from '@/libs/antd/props';
import formRule from '@/libs/antd/rules';
import { LoadingOutlined } from '@ant-design/icons';
import {
	SubmitCreditAnalystPayload,
	getProspectCustomerChecking,
	submitCreditAnalyst,
} from '@/libs/fleet-api/services/prospect';
import { getOrganisationStructure } from '@/libs/fleet-api/services/users';
import { useMutation, useQuery } from '@tanstack/react-query';
import {
	Button,
	Col,
	Divider,
	Form,
	Input,
	Result,
	Row,
	Select,
	Table,
} from 'antd';
import { FC, Fragment, useState } from 'react';
import fetcher from '@/libs/fleet-api/fetcher';
import GenericErrorMessage from '@/components/GenericErrorMessage';
import dayjs from 'dayjs';

// type SubmitCreditAnalystForm = {
// 	payload: Omit<SubmitCreditAnalystPayload, 'prospectId'>;
// };

export type CCDHAssignCCAPageProps = {
	prospectId: string;
};

const CCDHAssignCCAPage: FC<CCDHAssignCCAPageProps> = ({
	prospectId = '943P2024060009',
}) => {
	const { navigate, notification } = useApp();
	const {
		openCustomerDetail,
		openCustomerGroupDetail,
		openDocumentModal,
		openProspectDetail,
		openPreviewModal,
	} = useModals();
	const { data, isLoading, isFetching, isError } = useQuery({
		queryKey: ['getProspectCustomerChecking', prospectId] as const,
		queryFn: ({ queryKey }) => {
			const [, id] = queryKey;
			return getProspectCustomerChecking(id);
		},
	});

	const {
		mainInformation,
		customerCompanyManagementInformation,
		customerCompanyShareholderInformation,
		customerBusinessDocuments,
		customerLegalDocuments,
		customerReferralDocuments,
		approvalHistory,
		slikCheck,
	} = data?.data || {};

	const isJF = mainInformation?.customerType?.id === 1;

	const { form, fieldName } = useForm<SubmitCreditAnalystPayload>();

	const submitCreditAnalystMutation = useMutation({
		mutationFn: submitCreditAnalyst,
	});

	const [successModal, setSuccessModal] = useState(false);

	const selectCreditAnalyst = useQuerySelect({
		queryKey: ['getOrganisationStructure'],
		queryFn: async () => {
			const { data } = await getOrganisationStructure();
			return data.subordinates.map((cca) => {
				return {
					label: cca.fullName,
					value: cca.userId,
					data,
				};
			});
		},
		infinite: false,
	});

	if (isError) return <GenericErrorMessage />;

	if (isFetching) {
		return (
			<LoadingOutlined
				style={{
					marginTop: 160,
					fontSize: 100,
					color: '#1557BC',
					display: 'block',
				}}
			/>
		);
	}

	return (
		<Fragment>
			<FleetTitle>Main Information</FleetTitle>
			<DataRow
				loading={isLoading}
				style={{
					marginBottom: 20,
				}}
				items={[
					[
						[
							'Nama Customer',
							<a
								href="#"
								className="trigger-popup"
								onClick={(e) => {
									e.preventDefault();
									openCustomerDetail(mainInformation!.customerId);
								}}
							>
								{mainInformation?.customerName}
							</a>,
						],
						[
							'Prospect ID',
							<a
								href="#"
								className="trigger-popup"
								onClick={(e) => {
									e.preventDefault();
									openProspectDetail(mainInformation!.prospectId);
								}}
							>
								{mainInformation?.prospectId}
							</a>,
						],
					],
					[
						['Kelas Industri', mainInformation?.industrySector.industryClass],
						['Sumber Order', mainInformation?.orderSource],
					],
					[
						[
							'Portofolio Guideline',
							mainInformation?.industrySector.portofolioGuideline,
						],
						[
							'Group Customer BMPP',
							mainInformation?.customerGroup?.id ? (
								<a
									href="#"
									className="trigger-popup"
									onClick={(e) => {
										e.preventDefault();
										openCustomerGroupDetail(mainInformation!.customerGroup.id);
									}}
									aria-disabled={!mainInformation?.customerGroup.value}
								>
									{mainInformation?.customerGroup.value ?? '-'}
								</a>
							) : (
								'-'
							),
						],
					],
					[
						[
							'Dokumen',
							<a
								href="#"
								className="trigger-popup"
								onClick={(e) => {
									e.preventDefault();

									const documents: DocumentItem[] = [];
									const getDocumentType = (name: string) => {
										if (name.endsWith('pdf')) return 'pdf';
										if (name.endsWith('jpeg')) return 'jpeg';
										if (name.endsWith('jpg')) return 'jpg';
										if (name.endsWith('png')) return 'png';
									};

									if (
										customerBusinessDocuments &&
										customerBusinessDocuments.length
									) {
										customerBusinessDocuments
											.filter((doc) => doc.file)
											.forEach((document) => {
												documents.push({
													name: document.documentType,
													type: getDocumentType(document.file.name)!,
													url: document.file.url!,
												});
											});
									}

									if (customerLegalDocuments && customerLegalDocuments.length) {
										customerLegalDocuments
											.filter((doc) => doc.file)
											.forEach((document) => {
												documents.push({
													name: document.documentType,
													type: getDocumentType(document.file.name)!,
													url: document.file.url!,
												});
											});
									}

									if (
										customerReferralDocuments &&
										customerReferralDocuments.length
									) {
										customerReferralDocuments
											.filter((doc) => doc.file)
											.forEach((document) => {
												documents.push({
													name: document.documentType,
													type: getDocumentType(document.file.name)!,
													url: document.file.url!,
												});
											});
									}

									if (documents.length) {
										openDocumentModal(documents);
									} else {
										alert('no document found');
									}
								}}
							>
								View Dokumen
							</a>,
						],
					],
				]}
			/>

			<FleetTitle>View Data Checking SLIK</FleetTitle>
			{isJF && (
				<Fragment>
					<DataRow
						items={[
							[
								[
									'Status Pengecekan SLIK',
									slikCheck?.slikCheckingStatus?.value,
								],
								slikCheck?.slikCheckingStatus?.id === 1
									? [
											'Tanggal Pengecekan SLIK',
											slikCheck.slikCheckingDate &&
												dayjs(slikCheck.slikCheckingDate).format('DD/MM/YYYY'),
										]
									: undefined,
							],
						]}
					/>
					{slikCheck?.slikCheckingDate &&
						dayjs().diff(slikCheck.slikCheckingDate, 'day') > 90 && (
							<FormAlert
								alerts={[
									'Data SLIK debitur Anda sudah melewati masa berlaku, mohon untuk melakukan pembaruan data SLIK agar dapat melanjutkan proses.',
								]}
							/>
						)}
					<Divider />
				</Fragment>
			)}
			<DataRow
				items={[
					[
						[
							'View Form FAPP',
							slikCheck?.slikFormFile.id ? (
								<a
									href="#"
									onClick={(e) => {
										e.preventDefault();
										if (!slikCheck?.slikFormFile) return;
										openPreviewModal(slikCheck.slikFormFile);
									}}
								>
									Form FAPP
								</a>
							) : (
								'-'
							),
						],
						[
							'View Form PDP',
							slikCheck?.applicationLetterFile.id ? (
								<a
									href="#"
									onClick={(e) => {
										e.preventDefault();
										if (!slikCheck?.applicationLetterFile) return;
										openPreviewModal(slikCheck.applicationLetterFile);
									}}
								>
									Form-PDP
								</a>
							) : (
								'-'
							),
						],
					],
				]}
			/>
			<Divider />

			<FleetTitle>Management/Shareholder Listing</FleetTitle>
			<ViewManagementShareholder
				managements={customerCompanyManagementInformation ?? []}
				shareholders={customerCompanyShareholderInformation ?? []}
				style={{ marginBottom: 20 }}
			/>

			<FleetTitle>Approval History</FleetTitle>
			<Table
				bordered
				loading={isLoading}
				pagination={false}
				dataSource={
					approvalHistory?.map((approval, key) => {
						return {
							key,
							...approval,
						};
					}) ?? []
				}
				columns={[
					{
						title: 'No',
						render: (_, record) => record.no,
					},
					{
						title: 'Name',
						render: (_, record) => record.approvalBy,
					},
					{
						title: 'Date',
						render: (_, record) => record.approvalDate,
					},
					{
						title: 'Process',
						render: (_, record) => record.approvalProcess,
					},
					{
						title: 'Status',
						align: 'center',
						render: (_, record) => {
							return (
								<span
									className={`review-status ${record.approvalStatus.toLowerCase()}`}
								>
									{record.approvalStatus}
								</span>
							);
						},
					},
					{
						title: 'Approval Notes',
						render: (_, record) => record.approvalNote,
					},
				]}
				style={{ marginBottom: 20 }}
			/>

			<Divider />

			<Form
				{...ANTD_DEFAULT_PROPS.FORM}
				form={form}
				onFinish={async (values) => {
					try {
						// setSuccessModal(false);
						await submitCreditAnalystMutation.mutateAsync({
							...values,
							prospectId,
						});
						navigate('CCDHAssignCCAListPage');
						notification.success({
							message: 'Success',
							description: 'Data has been saved successfully',
						});
					} catch (error) {
						// setSuccessModal(false);
						if (fetcher.isError(error)) {
							const message = await error.json();
							notification.error({
								duration: 20,
								message: `Error ID: ${message?.metadata?.debugInfo?.errorId ?? 'Unknown'}`,
								description: (
									<div>
										{message?.metadata?.debugInfo?.messages?.length ? (
											<ul>
												{message?.metadata?.debugInfo?.messages.map(
													(msg: string) => <li key={msg}>{msg}</li>,
												)}
											</ul>
										) : (
											<pre>Opps, Something went wrong</pre>
										)}
									</div>
								),
							});
						}
					}
				}}
			>
				<Row gutter={[40, 20]}>
					<Col span={12}>
						<Form.Item
							name={fieldName(['creditAnalystId'])}
							label="Pilih Credit Analyst"
							rules={[formRule.required()]}
						>
							<Select
								{...selectCreditAnalyst}
								options={
									selectCreditAnalyst.options?.length
										? selectCreditAnalyst.options
										: []
								}
							/>
						</Form.Item>
					</Col>
				</Row>
				<Divider />
				<Row gutter={[40, 20]}>
					<Col span={24}>
						<Form.Item
							name={fieldName(['notes'])}
							label="Approval Notes"
							labelCol={{
								span: 6,
								style: {
									alignSelf: 'center',
								},
							}}
							wrapperCol={{
								style: {
									marginLeft: -10,
								},
							}}
							rules={[formRule.required()]}
						>
							<Input.TextArea rows={4} />
						</Form.Item>
					</Col>
				</Row>
				<Button
					type="primary"
					htmlType="submit"
					loading={submitCreditAnalystMutation.isPending}
					style={{
						width: 200,
						display: 'block',
						marginLeft: 'auto',
						marginTop: 60,
					}}
				>
					SAVE
				</Button>
			</Form>

			<FleetModal
				open={successModal}
				cancelText="BACK"
				okText="NEXT"
				onOk={() => {
					setSuccessModal(false);
					navigate('CCDHAssignCCAListPage');
				}}
				onCancel={() => {
					setSuccessModal(false);
					navigate('CCDHAssignCCAListPage');
				}}
			>
				<Result
					status="success"
					title={<div style={{ fontSize: 30 }}>Sukses</div>}
				/>
			</FleetModal>
		</Fragment>
	);
};

export default CCDHAssignCCAPage;
